import type {
  AdditionalAccountAvailability,
  AdditionalAccountAvailabilityItemResponse,
} from '../types/additionalAccountAvailability.type';
import { ADDITIONAL_ACCOUNT_HAS_OWN_ACCOUNT_NUMBER_KEY } from '../constants/additionalAccountAvailability.constant';

/**
 * Lee `hasOwnAccountNumber` del `additionalInfo` free-form sin confiar en su
 * forma: el campo puede venir ausente, y el blob entero puede ser un array, un
 * string o `null` según cómo Jackson serialice el `JsonNode`. Cualquier cosa
 * que no sea un booleano real devuelve `null` — nunca lanza, porque un 200 con
 * un `additionalInfo` inesperado no debe romper el drawer.
 */
function readHasOwnAccountNumber(additionalInfo: unknown): boolean | null {
  if (typeof additionalInfo !== 'object' || additionalInfo === null || Array.isArray(additionalInfo)) {
    return null;
  }

  const value = (additionalInfo as Record<string, unknown>)[ADDITIONAL_ACCOUNT_HAS_OWN_ACCOUNT_NUMBER_KEY];
  return typeof value === 'boolean' ? value : null;
}

/**
 * Guarda defensiva sobre `maxAdditionalAccounts`: el schema lo declara
 * `number`, pero el mismo criterio de `readHasOwnAccountNumber` aplica acá —
 * no confiar ciegamente en el contrato del backend. Cualquier cosa que no sea
 * un número real (string, `NaN`, `undefined`, `null`) cae a `null`.
 */
function readMaxAdditionalAccounts(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Indexa la configuración de disponibilidad por moneda, quedándose SOLO con lo
 * que la UI consume.
 *
 * El objeto resultante se construye desde cero con tres claves: así no hay
 * camino de código por el que el `additionalInfo` crudo —que trae notas
 * internas del equipo en `pending`— llegue al store, a un prop o al DOM. Hay
 * un test que asserta las claves exactas justamente para blindar eso.
 *
 * @param response - Array crudo del endpoint de availability.
 * @returns Mapa `moneda (mayúsculas) → disponibilidad`; `{}` si la respuesta no es un array.
 */
export function getAdditionalAccountAvailabilityAdapter(
  response: AdditionalAccountAvailabilityItemResponse[]
): Record<string, AdditionalAccountAvailability> {
  const items = Array.isArray(response) ? response : [];

  return items.reduce<Record<string, AdditionalAccountAvailability>>((availabilityByCurrency, item) => {
    const currency = String(item?.currency ?? '').trim().toUpperCase();
    // Ante dos filas de la misma moneda gana la PRIMERA: un reduce sin este
    // guard sería last-write-wins silencioso.
    if (!currency || availabilityByCurrency[currency]) {
      return availabilityByCurrency;
    }

    availabilityByCurrency[currency] = {
      currency,
      hasOwnAccountNumber: readHasOwnAccountNumber(item.additionalInfo),
      maxAdditionalAccounts: readMaxAdditionalAccounts(item.maxAdditionalAccounts),
    };
    return availabilityByCurrency;
  }, {});
}
